import React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { FormField, getFieldError } from '@/components/form/FormField';
import { ContractPicker } from '@/components/form/ContractPicker';
import {
  useContract,
  useCreateContract,
  useUpdateContract,
} from '../services/contractService';
import { useActiveVendors } from '../services/vendorService';
import { useUsers } from '../services/userService';
import { useDepartments } from '../services/departmentService';
import { ContractStatus, ContractType, PaymentFrequency, PAYMENT_FREQUENCY_LABELS } from '../types';
import { format, subDays } from 'date-fns';
import { parseCalendarDate, toCalendarIso, todayLocalMidnight } from '../utils/calendarDate';
import type { CreateContract, UpdateContract } from '../types';

/**
 * Contract form validation schema
 */
const contractSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title is too long'),
  contractNumber: z.string().min(1, 'Contract number is required').max(50, 'Contract number is too long'),
  vendorId: z.string().min(1, 'Vendor is required'),
  contractType: z.nativeEnum(ContractType),
  status: z.nativeEnum(ContractStatus),
  startDate: z.date({
    required_error: 'Start date is required',
    invalid_type_error: 'Invalid start date',
  }),
  endDate: z.date({
    required_error: 'End date is required',
    invalid_type_error: 'Invalid end date',
  }),
  renewalDate: z.date().optional().nullable(),
  totalContractValue: z.number().min(0, 'Total value must be positive'),
  annualContractValue: z.number().min(0, 'Annual value must be positive'),
  currency: z.string().length(3, 'Currency must be 3 characters (e.g., USD)'),
  autoRenews: z.boolean(),
  ownerUserId: z.string().optional(),
  departmentId: z.string().optional(),
  noticeRequiredDays: z
    .number({ invalid_type_error: 'Notice period must be a whole number of days' })
    .int('Notice period must be a whole number of days')
    .min(0, 'Notice period must be between 0 and 365 days')
    .max(365, 'Notice period must be between 0 and 365 days'),
  renewalTermMonths: z
    .number({ invalid_type_error: 'Renewal term must be a whole number of months' })
    .int('Renewal term must be a whole number of months')
    .min(1, 'Renewal term must be between 1 and 120 months')
    .max(120, 'Renewal term must be between 1 and 120 months')
    .nullable(),
  paymentFrequency: z.union([z.nativeEnum(PaymentFrequency), z.literal('')], {
    errorMap: () => ({ message: 'Choose a payment frequency from the list' }),
  }),
  parentContractId: z.string().optional(),
  description: z.string().max(2000, 'Description is too long').optional(),
  paymentTerms: z.string().max(2000, 'Payment terms is too long').optional(),
  notes: z.string().max(2000, 'Notes is too long').optional(),
});

type ContractFormValues = z.infer<typeof contractSchema>;

/**
 * Radix Select cannot carry an empty-string item value, so "no owner" / "no department"
 * is this sentinel in the picker and an empty string in form state.
 */
const NONE = '__none__';

const isKnownFrequency = (value: string): value is PaymentFrequency =>
  (Object.values(PaymentFrequency) as string[]).includes(value);

/**
 * Preview of the API's notice deadline. The picker holds local midnight of the picked day and the
 * API stores UTC midnight of that day (#65), so subtracting calendar days locally (subDays, safe
 * across a daylight-saving change) gives the same day as the server's EndDate - N days.
 */
const previewDeadline = (endDate: Date, noticeDays: number): Date => subDays(endDate, noticeDays);

/** Today and today + 1 year at local midnight: the dates a new contract starts with. */
const defaultStart = (): Date => todayLocalMidnight();
const defaultEnd = (): Date => {
  const today = todayLocalMidnight();
  return new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
};

/**
 * Contract Form Page Component
 */
const ContractForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams({ strict: false }) as { id?: string };
  const isEditMode = !!id;

  const { data: contract, isLoading: isLoadingContract } = useContract(id);
  const { data: vendors, isLoading: isLoadingVendors } = useActiveVendors();
  const { data: users, isLoading: isLoadingUsers } = useUsers();
  const { data: departments, isLoading: isLoadingDepartments } = useDepartments();
  const createMutation = useCreateContract();
  const updateMutation = useUpdateContract();

  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [formKey, setFormKey] = React.useState(0);
  // Display text for the chosen parent; the picker supplies it on selection, the loaded contract before that
  const [parentLabel, setParentLabel] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    setParentLabel(contract?.parentContractNumber ?? undefined);
  }, [contract?.parentContractNumber]);

  // Reset form when contract data loads
  React.useEffect(() => {
    if (contract && isEditMode) {
      setFormKey((prev) => prev + 1);
    }
  }, [contract, isEditMode]);

  const getDefaultValues = (): ContractFormValues => ({
    title: contract?.title || '',
    contractNumber: contract?.contractNumber || '',
    vendorId: contract?.vendorId || '',
    contractType: contract?.contractType || ContractType.SaaSSubscription,
    status: contract?.status || ContractStatus.Draft,
    // Calendar days (#65): never new Date(iso), which is the previous evening west of UTC
    startDate: parseCalendarDate(contract?.startDate) ?? defaultStart(),
    endDate: parseCalendarDate(contract?.endDate) ?? defaultEnd(),
    renewalDate: parseCalendarDate(contract?.renewalDate),
    totalContractValue: contract?.totalContractValue || 0,
    annualContractValue: contract?.annualContractValue || 0,
    currency: contract?.currency || 'USD',
    autoRenews: contract?.autoRenews || false,
    ownerUserId: contract?.ownerUserId ?? '',
    departmentId: contract?.departmentId ?? '',
    noticeRequiredDays: contract?.noticeRequiredDays ?? 0,
    renewalTermMonths: contract?.renewalTermMonths ?? null,
    // A legacy value outside the set is kept so the field shows it; the schema flags it until changed
    paymentFrequency: (contract?.paymentFrequency ?? '') as ContractFormValues['paymentFrequency'],
    parentContractId: contract?.parentContractId ?? '',
    description: contract?.description || '',
    paymentTerms: contract?.paymentTerms || '',
    notes: contract?.notes || '',
  });

  const form = useForm({
    defaultValues: getDefaultValues(),
    onSubmit: async ({ value }) => {
      try {
        setSubmitError(null);

        const payload = {
          ...value,
          startDate: toCalendarIso(value.startDate),
          endDate: toCalendarIso(value.endDate),
          renewalDate: value.renewalDate ? toCalendarIso(value.renewalDate) : undefined,
          ownerUserId: value.ownerUserId || undefined,
          departmentId: value.departmentId || undefined,
          renewalTermMonths: value.renewalTermMonths ?? undefined,
          paymentFrequency: value.paymentFrequency || undefined,
          parentContractId: value.parentContractId || undefined,
        };

        if (isEditMode && id) {
          const updated = await updateMutation.mutateAsync({
            id,
            data: payload as UpdateContract,
          });
          navigate({ to: `/contracts/${updated.id}` });
        } else {
          const created = await createMutation.mutateAsync(payload as CreateContract);
          navigate({ to: `/contracts/${created.id}` });
        }
      } catch (error) {
        console.error('Failed to save contract:', error);
        setSubmitError('Failed to save contract. Please try again.');
      }
    },
  });

  // Active rows for new assignments, plus the current selection so an owner or department
  // deactivated after assignment stays visible and re-saves unchanged.
  const ownerOptions = (users ?? []).filter((u) => u.isActive || u.id === contract?.ownerUserId);
  const departmentOptions = (departments ?? []).filter((d) => d.isActive || d.id === contract?.departmentId);

  const handleBack = () => {
    if (isEditMode && id) {
      navigate({ to: `/contracts/${id}` });
    } else {
      navigate({ to: '/contracts' });
    }
  };

  // Loading state
  if ((isEditMode && isLoadingContract) || isLoadingVendors || isLoadingUsers || isLoadingDepartments) {
    return (
      <div className="max-w-5xl mx-auto py-8 px-4">
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      {/* Page Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={handleBack} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <h1 className="text-3xl font-semibold text-grey-900">
          {isEditMode ? 'Edit Contract' : 'New Contract'}
        </h1>
        {isEditMode && contract && (
          <p className="text-grey-500 mt-1">
            {contract.contractNumber} - {contract.title}
          </p>
        )}
      </div>

      {submitError && (
        <Alert variant="destructive" className="mb-6">
          {submitError}
        </Alert>
      )}

      <form
        key={formKey}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="grid grid-cols-12 gap-6">
          {/* Basic Information */}
          <div className="col-span-12">
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-grey-900 mb-6">
                Basic Information
              </h2>
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <form.Field name="title" validators={{ onChange: contractSchema.shape.title }}>
                    {(field) => (
                      <FormField label="Contract Title" required error={getFieldError(field.state.meta.errors)}>
                        <Input
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          placeholder="e.g., Microsoft Office 365 Enterprise License"
                        />
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12 md:col-span-6">
                  <form.Field name="contractNumber" validators={{ onChange: contractSchema.shape.contractNumber }}>
                    {(field) => (
                      <FormField label="Contract Number" required error={getFieldError(field.state.meta.errors)}>
                        <Input
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          placeholder="e.g., CNT-2024-001"
                        />
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12 md:col-span-6">
                  <form.Field name="vendorId" validators={{ onChange: contractSchema.shape.vendorId }}>
                    {(field) => (
                      <FormField label="Vendor" required error={getFieldError(field.state.meta.errors)}>
                        <Select value={field.state.value} onValueChange={field.handleChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Vendor" />
                          </SelectTrigger>
                          <SelectContent>
                            {vendors?.map((vendor) => (
                              <SelectItem key={vendor.id} value={vendor.id}>
                                {vendor.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12 md:col-span-6">
                  <form.Field name="contractType" validators={{ onChange: contractSchema.shape.contractType }}>
                    {(field) => (
                      <FormField label="Contract Type" required error={getFieldError(field.state.meta.errors)}>
                        <Select value={field.state.value} onValueChange={(val) => field.handleChange(val as ContractType)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select contract type" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(ContractType).map((type) => (
                              <SelectItem key={type} value={type}>
                                {type.replace(/([A-Z])/g, ' $1').trim()}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12 md:col-span-6">
                  <form.Field name="status" validators={{ onChange: contractSchema.shape.status }}>
                    {(field) => (
                      <FormField label="Status" required error={getFieldError(field.state.meta.errors)}>
                        <Select value={field.state.value} onValueChange={(val) => field.handleChange(val as ContractStatus)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(ContractStatus).map((status) => (
                              <SelectItem key={status} value={status}>
                                {status.replace(/([A-Z])/g, ' $1').trim()}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12 md:col-span-6">
                  <form.Field name="ownerUserId">
                    {(field) => (
                      <FormField label="Owner" error={getFieldError(field.state.meta.errors)} helperText="Who is accountable for this contract">
                        <Select
                          value={field.state.value || NONE}
                          onValueChange={(val) => field.handleChange(val === NONE ? '' : val)}
                        >
                          <SelectTrigger aria-label="Owner">
                            <SelectValue placeholder="Select owner" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>None</SelectItem>
                            {ownerOptions.map((user) => (
                              <SelectItem key={user.id} value={user.id}>
                                {user.displayName}
                                {user.isActive ? '' : ' (inactive)'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12 md:col-span-6">
                  <form.Field name="departmentId">
                    {(field) => (
                      <FormField label="Department" error={getFieldError(field.state.meta.errors)} helperText="Department that holds the budget">
                        <Select
                          value={field.state.value || NONE}
                          onValueChange={(val) => field.handleChange(val === NONE ? '' : val)}
                        >
                          <SelectTrigger aria-label="Department">
                            <SelectValue placeholder="Select department" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>None</SelectItem>
                            {departmentOptions.map((department) => (
                              <SelectItem key={department.id} value={department.id}>
                                {department.name}
                                {department.isActive ? '' : ' (inactive)'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12">
                  <form.Field name="parentContractId">
                    {(field) => (
                      <FormField
                        label="Parent Contract"
                        error={getFieldError(field.state.meta.errors)}
                        helperText="Set when this contract amends another one; the contract itself and its amendments are not offered"
                      >
                        <ContractPicker
                          value={field.state.value ?? ''}
                          label={parentLabel}
                          excludeSubtreeOf={isEditMode ? id : undefined}
                          onChange={(nextId, nextLabel) => {
                            field.handleChange(nextId);
                            setParentLabel(nextLabel);
                          }}
                        />
                      </FormField>
                    )}
                  </form.Field>
                </div>
              </div>
            </Card>
          </div>

          {/* Dates */}
          <div className="col-span-12 md:col-span-6">
            <Card className="p-6 h-full">
              <h2 className="text-lg font-semibold text-grey-900 mb-6">
                Contract Dates
              </h2>
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <form.Field name="startDate" validators={{ onChange: contractSchema.shape.startDate }}>
                    {(field) => (
                      <FormField label="Start Date" required error={getFieldError(field.state.meta.errors)}>
                        <DatePicker
                          value={field.state.value}
                          onChange={(date) => field.handleChange(date || todayLocalMidnight())}
                        />
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12">
                  <form.Field name="endDate" validators={{ onChange: contractSchema.shape.endDate }}>
                    {(field) => (
                      <FormField label="End Date" required error={getFieldError(field.state.meta.errors)}>
                        <DatePicker
                          value={field.state.value}
                          onChange={(date) => field.handleChange(date || todayLocalMidnight())}
                        />
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12">
                  <form.Field name="renewalDate" validators={{ onChange: contractSchema.shape.renewalDate }}>
                    {(field) => (
                      <FormField label="Renewal Date (Optional)" error={getFieldError(field.state.meta.errors)} helperText="When to start renewal process">
                        <DatePicker
                          value={field.state.value}
                          onChange={(date) => field.handleChange(date)}
                        />
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12">
                  <form.Field name="autoRenews">
                    {(field) => (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={field.state.value}
                          onCheckedChange={(checked) => field.handleChange(checked === true)}
                          id="autoRenews"
                        />
                        <Label htmlFor="autoRenews">Contract auto-renews at end date</Label>
                      </div>
                    )}
                  </form.Field>
                </div>
              </div>
            </Card>
          </div>

          {/* Financial Information */}
          <div className="col-span-12 md:col-span-6">
            <Card className="p-6 h-full">
              <h2 className="text-lg font-semibold text-grey-900 mb-6">
                Financial Information
              </h2>
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <form.Field name="totalContractValue" validators={{ onChange: contractSchema.shape.totalContractValue }}>
                    {(field) => (
                      <FormField label="Total Contract Value" required error={getFieldError(field.state.meta.errors)} helperText="Full value over contract term">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-grey-500 text-sm">$</span>
                          <Input
                            className="pl-7"
                            type="number"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(parseFloat(e.target.value) || 0)}
                            onBlur={field.handleBlur}
                            min={0}
                            step={0.01}
                          />
                        </div>
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12">
                  <form.Field name="annualContractValue" validators={{ onChange: contractSchema.shape.annualContractValue }}>
                    {(field) => (
                      <FormField label="Annual Value" required error={getFieldError(field.state.meta.errors)} helperText="Yearly cost for budgeting">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-grey-500 text-sm">$</span>
                          <Input
                            className="pl-7"
                            type="number"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(parseFloat(e.target.value) || 0)}
                            onBlur={field.handleBlur}
                            min={0}
                            step={0.01}
                          />
                        </div>
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12">
                  <form.Field name="currency" validators={{ onChange: contractSchema.shape.currency }}>
                    {(field) => (
                      <FormField label="Currency" required error={getFieldError(field.state.meta.errors)}>
                        <Select value={field.state.value} onValueChange={field.handleChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD - US Dollar</SelectItem>
                            <SelectItem value="EUR">EUR - Euro</SelectItem>
                            <SelectItem value="GBP">GBP - British Pound</SelectItem>
                            <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                            <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12">
                  <form.Field name="paymentFrequency" validators={{ onChange: contractSchema.shape.paymentFrequency }}>
                    {(field) => {
                      const current = field.state.value;
                      const legacy = current !== '' && !isKnownFrequency(current);
                      return (
                        <FormField
                          label="Payment Frequency"
                          error={
                            getFieldError(field.state.meta.errors) ??
                            (legacy ? `"${current}" is no longer a valid payment frequency; choose one from the list` : undefined)
                          }
                        >
                          <Select
                            value={current || NONE}
                            onValueChange={(val) => field.handleChange((val === NONE ? '' : val) as ContractFormValues['paymentFrequency'])}
                          >
                            <SelectTrigger aria-label="Payment Frequency">
                              <SelectValue placeholder="Select payment frequency" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NONE}>Not specified</SelectItem>
                              {legacy && (
                                <SelectItem value={current} disabled>
                                  {current} (no longer valid)
                                </SelectItem>
                              )}
                              {Object.values(PaymentFrequency).map((frequency) => (
                                <SelectItem key={frequency} value={frequency}>
                                  {PAYMENT_FREQUENCY_LABELS[frequency]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormField>
                      );
                    }}
                  </form.Field>
                </div>
              </div>
            </Card>
          </div>

          {/* Renewal and Notice */}
          <div className="col-span-12">
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-grey-900 mb-6">
                Renewal and Notice
              </h2>
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12 md:col-span-6">
                  <form.Field name="noticeRequiredDays" validators={{ onChange: contractSchema.shape.noticeRequiredDays }}>
                    {(field) => (
                      <FormField
                        label="Notice Required (days)"
                        required
                        error={getFieldError(field.state.meta.errors)}
                        helperText="Days before the end date by which notice must be given (0 to 365; 0 means none)"
                      >
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={365}
                          step={1}
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value === '' ? 0 : Number(e.target.value))}
                          onBlur={field.handleBlur}
                          aria-label="Notice Required (days)"
                        />
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12 md:col-span-6">
                  <form.Field name="renewalTermMonths" validators={{ onChange: contractSchema.shape.renewalTermMonths }}>
                    {(field) => (
                      <FormField
                        label="Renewal Term (months)"
                        error={getFieldError(field.state.meta.errors)}
                        helperText="Length of each renewal when the contract auto-renews (1 to 120; leave empty if none)"
                      >
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={120}
                          step={1}
                          value={field.state.value ?? ''}
                          onChange={(e) => field.handleChange(e.target.value === '' ? null : Number(e.target.value))}
                          onBlur={field.handleBlur}
                          aria-label="Renewal Term (months)"
                        />
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12">
                  <form.Subscribe selector={(state) => [state.values.endDate, state.values.noticeRequiredDays] as const}>
                    {([endDate, noticeDays]) => {
                      const valid = noticeDays > 0 && noticeDays <= 365 && endDate instanceof Date && !Number.isNaN(endDate.getTime());
                      return (
                        <p className="text-sm text-grey-500" data-testid="notice-deadline-preview">
                          {valid
                            ? `Notice deadline preview: ${format(previewDeadline(endDate, noticeDays), 'MMM dd, yyyy')} (the saved contract shows the authoritative date)`
                            : 'No notice deadline: notice period is 0 days'}
                        </p>
                      );
                    }}
                  </form.Subscribe>
                </div>
              </div>
            </Card>
          </div>

          {/* Additional Information */}
          <div className="col-span-12">
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-grey-900 mb-6">
                Additional Information
              </h2>
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <form.Field name="description" validators={{ onChange: contractSchema.shape.description }}>
                    {(field) => (
                      <FormField label="Description" error={getFieldError(field.state.meta.errors)}>
                        <Textarea
                          rows={4}
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          placeholder="Describe the contract scope, deliverables, and key terms..."
                        />
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12 md:col-span-6">
                  <form.Field name="paymentTerms" validators={{ onChange: contractSchema.shape.paymentTerms }}>
                    {(field) => (
                      <FormField label="Payment Terms" error={getFieldError(field.state.meta.errors)}>
                        <Textarea
                          rows={3}
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          placeholder="e.g., Net 30, Annual prepayment..."
                        />
                      </FormField>
                    )}
                  </form.Field>
                </div>

                <div className="col-span-12 md:col-span-6">
                  <form.Field name="notes" validators={{ onChange: contractSchema.shape.notes }}>
                    {(field) => (
                      <FormField label="Internal Notes" error={getFieldError(field.state.meta.errors)}>
                        <Textarea
                          rows={3}
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          placeholder="Internal notes (not visible to vendors)..."
                        />
                      </FormField>
                    )}
                  </form.Field>
                </div>
              </div>
            </Card>
          </div>

          {/* Form Actions */}
          <div className="col-span-12">
            <Card className="p-6">
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={handleBack}
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="lg"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : isEditMode
                    ? 'Update Contract'
                    : 'Create Contract'}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContractForm;
